import React from 'react';
import { GatsbyImage, getImage } from 'gatsby-plugin-image'
import get from 'lodash/get';
import { BLOCKS, MARKS, INLINES } from "@contentful/rich-text-types";

const Bold = ({ children }) => <b>{children}</b>;
const Code = ({ children }) => <span className="code-block" dangerouslySetInnerHTML={{ __html: children }}/>;

const options = (props = {}) => ({
  renderMark: {
    [MARKS.BOLD]: text => <Bold>{text}</Bold>,
    [MARKS.CODE]: code => {
      if (props.Code) {
        return <props.Code>{code}</props.Code>;
      }
      return <Code>{code}</Code>;
    },
  },
  renderNode: {
    [BLOCKS.UL_LIST]: (node, children) => {
      if (props.UlList) {
        return <props.UlList>{children}</props.UlList>;
      }

      return <ul>{children}</ul>;
    },
    [BLOCKS.LIST_ITEM]: (node, children) => {
      if (props.ListItem) {
        return <props.ListItem>{children}</props.ListItem>;
      }

      return <li>{children}</li>;
    },
    [BLOCKS.PARAGRAPH]: (node, children) => {
      let ignore = false;

      const childType = get(children, '[0].type.name');
      if (childType === 'Code') return <React.Fragment>{children}</React.Fragment>;

      Object.keys(children).forEach((child) => {
        if (typeof children[child] === 'object' && get(children[child], 'props.className') === 'video-wrapper') {
          ignore = true;
        }
      });

      if (ignore) {
        return children;
      }

      if (props.Text) {
        return (
          <props.Text>{children}</props.Text>
        );
      }

      return <p>{children}</p>;
    },
    [BLOCKS.HEADING_1]: (node, children) => {
      if (props.HeadingOne) {
        return <props.HeadingOne>{children}</props.HeadingOne>;
      }

      return <h1>{children}</h1>;
    },
    [BLOCKS.HEADING_2]: (node, children) => {
      if (props.HeadingTwo) {
        return <props.HeadingTwo>{children}</props.HeadingTwo>;
      }

      return <h2>{children}</h2>;
    },
    [BLOCKS.HEADING_3]: (node, children) => {
      if (props.HeadingThree) {
        return <props.HeadingThree>{children}</props.HeadingThree>;
      }

      return <h3>{children}</h3>;
    },
    [BLOCKS.HEADING_4]: (node, children) => {
      if (props.HeadingFour) {
        return <props.HeadingFour>{children}</props.HeadingFour>;
      }

      return <h4>{children}</h4>;
    },
    [BLOCKS.HEADING_5]: (node, children) => {
      if (props.HeadingFive) {
        return <props.HeadingFive>{children}</props.HeadingFive>;
      }

      return <h5>{children}</h5>;
    },
    [BLOCKS.HEADING_6]: (node, children) => {
      if (props.HeadingSix) {
        return <props.HeadingSix>{children}</props.HeadingSix>;
      }

      return <h6>{children}</h6>;
    },
    [INLINES.HYPERLINK]: (node) => {
      if (node.data.uri.indexOf('youtube.com') !== -1) {
        return (
          <div className="video-wrapper">
            <iframe
              title="text-video"
              src={node.data.uri}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        );
      }

      const nofollow = (node.data.uri.indexOf('profilepensions.co.uk') !== -1) ? '' : 'nofollow ';

      if (props.Hyperlink) {
        return (
          <props.Hyperlink href={node.data.uri} target="_blank" rel={`${nofollow}noreferrer noopener`}>
            {node.content[0].value}
          </props.Hyperlink>
        );
      }

      return (
        <a href={node.data.uri} target="_blank" rel={`${nofollow}noreferrer noopener`}>
          {node.content[0].value}
        </a>
      );
    },
    [BLOCKS.EMBEDDED_ASSET]: (node) => {
      const target = get(node, 'data.target', null);
      if (!node) {
        return null;
      }

      const { gatsbyImageData, title } = target;

      return (
        <GatsbyImage
          image={getImage(gatsbyImageData)}
          objectFit='contain'
          objectPosition="0% 50%"
          alt={title}
        />
      )
    },
  },
});

const elementsExist = (elements) => ((elements && elements.length > 1) || elements[0]);

const headingOne = (className) => ({ children }) => elementsExist(children) ? <h1 className={`${className}`}>{children}</h1> : null;
const headingTwo = (className) => ({ children }) => elementsExist(children) ? <h2 className={`${className}`}>{children}</h2> : null;
const headingThree = (className) => ({ children }) => elementsExist(children) ? <h3 className={`${className}`}>{children}</h3> : null;
const headingFour = (className) => ({ children }) => elementsExist(children) ? <h4 className={`${className}`}>{children}</h4> : null;
const headingFive = (className) => ({ children }) => elementsExist(children) ? <h5 className={`${className}`}>{children}</h5> : null;
const headingSix = (className) => ({ children }) => elementsExist(children) ? <h6 className={`${className}`}>{children}</h6> : null;
const text = (className) => ({ children }) => elementsExist(children) ? <p className={`${className}`}>{children}</p> : null;
const hyperlink = (className) => ({ children, ...props }) => elementsExist(children) ? <a href={props.href} target={props.target} rel={props.rel} className={`${className}`}>{children}</a> : null;
const ulList = (className) => ({ children }) => elementsExist(children) ? <ul className={`${className}`}>{children}</ul> : null;

const styledBlocks = (className) => ({
  HeadingOne: headingOne(className),
  HeadingTwo: headingTwo(className),
  HeadingThree: headingThree(className),
  HeadingFour: headingFour(className),
  HeadingFive: headingFive(className),
  HeadingSix: headingSix(className),
  Text: text(className),
  Hyperlink: hyperlink(className),
  UlList: ulList(className),
});

export default options;
export {
  headingOne,
  headingTwo,
  headingThree,
  headingFour,
  headingFive,
  headingSix,
  text,
  styledBlocks,
  elementsExist,
};
